import { memo } from 'react'
import { useTranslation } from 'react-i18next'

const CREATION_STEPS = [
  { id: 'analyzing', labelKey: 'styleCreatorProgress.analyzing' },
  { id: 'synthesizing', labelKey: 'styleCreatorProgress.synthesizing' },
  { id: 'generating_images', labelKey: 'styleCreatorProgress.generatingImages' },
  { id: 'finalizing', labelKey: 'styleCreatorProgress.finalizing' },
]

interface StyleCreatorProgressProps {
  styleName: string
  currentStep: string
  progress: number
}

export const StyleCreatorProgress = memo(
  ({ currentStep, progress }: StyleCreatorProgressProps) => {
    const { t } = useTranslation('canvas')
    const currentIndex = CREATION_STEPS.findIndex((s) => s.id === currentStep)

    return (
      <div
        className="flex flex-col items-center justify-center py-12 px-8"
        data-testid="style-creator-progress"
      >
        <div className="w-full max-w-sm flex flex-col">
          {CREATION_STEPS.map((step, i) => {
            const state =
              i < currentIndex
                ? 'completed'
                : i === currentIndex
                  ? 'active'
                  : 'pending'
            return (
              <div
                key={step.id}
                className="flex items-center gap-3 py-2"
                data-testid={`progress-step-${step.id}`}
              >
                {state === 'completed' ? (
                  <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 animate-check-in">
                    <svg
                      className="w-3 h-3 text-white"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="6 12 10 16 18 8" />
                    </svg>
                  </div>
                ) : state === 'active' ? (
                  <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0">
                    <div className="w-3 h-3 rounded-full bg-pink-500 animate-pulse" />
                  </div>
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-foreground/20 flex-shrink-0" />
                )}

                <span
                  className={
                    state === 'completed'
                      ? 'text-sm text-foreground'
                      : state === 'active'
                        ? 'text-sm text-foreground font-semibold'
                        : 'text-sm text-foreground/30'
                  }
                >
                  {t(step.labelKey)}
                  {state === 'active' ? '...' : ''}
                </span>
              </div>
            )
          })}
        </div>

        <div className="mt-6 w-full max-w-sm">
          <div className="w-full h-2 bg-surface-2 rounded-full overflow-hidden">
            <div
              className="h-full bg-pink-500 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${Math.round(progress * 100)}%` }}
            />
          </div>
          <p className="text-xs font-medium text-foreground/40 mt-1 text-right">
            {Math.round(progress * 100)}%
          </p>
        </div>

        <p className="text-xs font-medium text-foreground/40 mt-4 text-center">
          {t('styleCreatorProgress.timeEstimate')}
        </p>
      </div>
    )
  },
)
StyleCreatorProgress.displayName = 'StyleCreatorProgress'
